import { UserProgram } from '@policyfly/protobuf/patrick'
import { Domain, Role } from '@policyfly/protobuf/pfbingo'

import type { PiniaPlugin } from 'pinia'

type ApiStore = ReturnType<typeof import('@/stores/api')['useApiStore']>
type ApplicationListState = ReturnType<typeof import('@/stores/applicationList')['useApplicationListStore']>['$state']
type AuthenticationState = ReturnType<typeof import('@/stores/authentication')['useAuthenticationStore']>['$state']
type FilterState = ReturnType<typeof import('@/stores/filter')['useFilterStore']>['$state']
type CmsState = ReturnType<typeof import('@/stores/cms')['useCmsStore']>['$state']
type ScheduleState = ReturnType<typeof import('@/stores/schedule')['useScheduleStore']>['$state']
export type PersistedState = {
  api: Pick<ApiStore, 'refreshToken' | 'spaToken' | 'spaTokenExpiration'>
  applicationList: Pick<ApplicationListState, 'rowsPerPage' | 'userHeaders'>
  authentication: Pick<AuthenticationState, 'legacyId' | 'domains' | 'programIds' | 'bingoId'>
  filter: Pick<FilterState, 'filters' | 'page' | 'sortBy' | 'search'>
  cms: Pick<CmsState, 'lastReadTimestamp'>
  schedule: Pick<ScheduleState, 'sessionStates'>
}

export const VUEX_STORAGE_KEY = 'vuex'
export const PINIA_STORAGE_KEY = 'pinia'

interface LegacyProgram {
  subscription: string
  default_program: boolean
  program: {
    id: number
    name: string
    slug: string
    client: string
  }
}

function subscriptionToRole (subscription: string): Role {
  switch (subscription) {
    case 'STAFF': return Role.ROLE_STAFF
    case 'SUPERUSER': return Role.ROLE_SUPERUSER
    case 'PROGRAM_ADMIN': return Role.ROLE_PROGRAM_ADMIN
    case 'UNDERWRITER': return Role.ROLE_UNDERWRITER
    case 'TECHNICIAN': return Role.ROLE_TECHNICIAN
    case 'QUALITY_CONTROLLER': return Role.ROLE_QUALITY_CONTROL
    case 'READ_ONLY': return Role.ROLE_AUDITOR
    case 'ISSUING_AGENT': return Role.ROLE_ISSUING_AGENT
    default: return Role.ROLE_DISTRIBUTION_PARTNER
  }
}

export function createPersistedStatePlugin (): PiniaPlugin {
  // backwards compatibility for state previously stored with vuex
  const vuexState = JSON.parse(localStorage.getItem(VUEX_STORAGE_KEY) || '{}')
  const piniaState = JSON.parse(localStorage.getItem(PINIA_STORAGE_KEY) || '{}')
  const currentState: PersistedState = {
    api: piniaState.api || vuexState.api || {},
    applicationList: piniaState.applicationList || vuexState.application?.list || {},
    authentication: piniaState.authentication || vuexState.authentication || {},
    schedule: piniaState.schedule || vuexState.schedule || {},
    filter: piniaState.filter || vuexState.filter || {},
    cms: piniaState.cms || vuexState.cms || {},
  }

  // save the combined state so any vuex state isn't lost
  localStorage.setItem(PINIA_STORAGE_KEY, JSON.stringify(currentState))

  return ({ store }) => {
    const id = store.$id as keyof PersistedState
    if (!(id in currentState)) return

    // hydrate if a persisted store is being registered
    const prevState = currentState[id] as Partial<typeof store.$state>
    if (prevState) {
      // migrate any previous state
      switch (id) {
        case 'filter':
          if (typeof prevState.sortBy === 'string') {
            prevState.sortBy = [{ key: prevState.sortBy, order: prevState.sortDesc ? 'desc' : 'asc' }]
          }
          break
        case 'authentication':
          if (Array.isArray(prevState.programs) && prevState.programs.length) {
            if ('program' in prevState.programs[0]) {
              prevState.programs = prevState.programs.map<UserProgram>((p: LegacyProgram) => {
                return UserProgram.create({
                  id: p.program.id,
                  defaultProgram: p.default_program,
                  role: subscriptionToRole(p.subscription),
                  client: p.program.client,
                  name: p.program.name,
                  slug: p.program.slug,
                })
              })
            }

            prevState.domains = [{
              domain: Domain.DOMAIN_PF_CORE,
              programs: prevState.programs.map((p: UserProgram | (Omit<UserProgram, 'role'> & { role: string })) => {
                if (typeof p.role === 'string') {
                  return { ...p, role: subscriptionToRole(p.role) }
                }
                return p
              }),
              agencies: [],
            }]
            delete prevState.programs
          }

          if (prevState.programId) {
            prevState.programIds = {
              id: prevState.programId,
            }
            delete prevState.programId
          }

          if (prevState.userId) {
            prevState.legacyId = prevState.userId
            delete prevState.userId
          }
      }
      store.$patch(prevState)
    }

    store.$subscribe((event, state) => {
      const storeId = event.storeId as keyof PersistedState
      switch (storeId) {
        case 'api': {
          const apiState = state as ApiStore
          currentState.api = {
            refreshToken: apiState.refreshToken,
            spaToken: apiState.spaToken,
            spaTokenExpiration: apiState.spaTokenExpiration,
          }
          break
        }
        case 'applicationList': {
          const applicationListState = state as ApplicationListState
          currentState.applicationList = {
            rowsPerPage: applicationListState.rowsPerPage,
            userHeaders: applicationListState.userHeaders,
          }
          break
        }
        case 'authentication': {
          const authenticationState = state as AuthenticationState
          currentState.authentication = {
            legacyId: authenticationState.legacyId,
            domains: authenticationState.domains,
            programIds: authenticationState.programIds,
            bingoId: authenticationState.bingoId,
          }
          break
        }
        case 'cms': {
          const cmsState = state as CmsState
          currentState.cms = { lastReadTimestamp: cmsState.lastReadTimestamp }
          break
        }
        case 'filter': {
          const filterState = state as FilterState
          currentState.filter = {
            filters: filterState.filters,
            page: filterState.page,
            sortBy: filterState.sortBy,
            search: filterState.search,
          }
          break
        }
        case 'schedule': {
          const scheduleState = state as ScheduleState
          // persist only schedule session state data used to bootstrap future loads
          const sessionStates: typeof scheduleState.sessionStates = {}

          for (const [scheduleId, sessionState] of Object.entries(scheduleState.sessionStates)) {
            sessionStates[scheduleId] = {
              ...sessionState,
              pages: [],
            }
          }

          currentState.schedule = {
            sessionStates,
          }
          break
        }
      }
      localStorage.setItem(PINIA_STORAGE_KEY, JSON.stringify(currentState))
    }, { detached: true })
  }
}
